/**
 * @module features/files/file-list
 * @description Manages file list rendering and highlighting
 */

import { createFileItem } from './file-item.js';
import { getDOMElements } from '../../ui/dom/dom-manager.js';
import { logger } from '../../utils/logger.js';
import { setSearchableFiles } from './file-search.js';

/**
 * Strip entity prefix from file ID (e.g., "project_123" -> "123")
 * @param {string} fileId - File ID that may have entity prefix
 * @returns {string} Clean file ID without prefix
 */
function stripEntityPrefix(fileId) {
    if (!fileId) return fileId;
    const match = fileId.match(/^[a-z]+_(.+)$/i);
    return match ? match[1] : fileId;
}

/**
 * Display a list of files in the files container
 * @param {Array} files - Array of file objects
 * @param {Object} user - Firebase user object
 * @param {string|null} highlightFileId - ID of file to highlight
 * @param {string} entityType - Entity type (projects, opportunities, leads, contacts)
 * @param {string|null} entityId - Entity ID
 * @returns {Promise<void>} Resolves when all files are rendered
 */
export async function displayFiles(files, user, highlightFileId = null, entityType = 'projects', entityId = null) {
    const dom = getDOMElements;
    const filesContainer = dom.filesContainer;

    filesContainer.innerHTML = '';

    if (!files || files.length === 0) {
        filesContainer.innerHTML = '<p>No files found in this archive.</p>';
        // Update searchable files (empty)
        setSearchableFiles([]);
        return;
    }

    // Update searchable files for search functionality
    setSearchableFiles(files);

    // Create all file items in parallel and await them
    const fileItemPromises = files.map(file => {
        const fileId = file.archivedFileObjectId;
        // Strip entity prefixes and compare (e.g., "project_123" matches "123")
        const cleanFileId = stripEntityPrefix(String(fileId));
        const cleanHighlightId = stripEntityPrefix(String(highlightFileId || ''));
        const shouldHighlight = highlightFileId && cleanFileId === cleanHighlightId;

        if (highlightFileId && shouldHighlight) {
            logger.debug(`✨ Highlighting file: ${fileId} (matches ${highlightFileId})`);
        }

        return createFileItem(
            file,
            user,
            shouldHighlight,
            entityType,
            entityId
        );
    });

    // Wait for all file items to be created (permission checks are async)
    const fileItems = await Promise.all(fileItemPromises);

    // Append all file items to the container
    fileItems.forEach(fileItem => {
        filesContainer.appendChild(fileItem);
    });
}

/**
 * Highlight a specific file in the list and scroll to it
 * @param {string} fileId - ID of the file to highlight
 */
export function highlightFile(fileId) {
    if (!fileId) {
        logger.warn('⚠️ highlightFile called without fileId');
        return;
    }

    logger.debug(`🔍 Looking for file to highlight: ${fileId}`);

    setTimeout(() => {
        // Try to find element with exact match first, then try with common prefixes
        let fileElement = document.querySelector(`[data-file-id="${fileId}"]`);

        if (!fileElement) {
            // Try with entity prefixes if not found
            const prefixes = ['project_', 'opportunity_', 'lead_', 'contact_'];
            for (const prefix of prefixes) {
                fileElement = document.querySelector(`[data-file-id="${prefix}${fileId}"]`);
                if (fileElement) {
                    logger.debug(`✅ Found file with prefix: ${prefix}${fileId}`);
                    break;
                }
            }
        }

        if (fileElement) {
            logger.debug(`✅ Found file element, applying highlight and scroll`);

            // Ensure the highlight class is present (in case it was missed during render)
            if (!fileElement.classList.contains('file-item-highlight')) {
                fileElement.classList.add('file-item-highlight');
            }

            fileElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

            // Add a temporary pulse effect
            fileElement.classList.add('file-item-pulse');
            setTimeout(() => {
                fileElement.classList.remove('file-item-pulse');
            }, 2400); // Extended to 2.4s to match the 4 pulse cycles
        } else {
            logger.error(`❌ File element not found for fileId: ${fileId}`);
            // Log all available file IDs for debugging
            const allFiles = document.querySelectorAll('[data-file-id]');
            logger.debug(`Available file IDs (${allFiles.length}):`,
                Array.from(allFiles).map(el => el.getAttribute('data-file-id')));
        }
    }, 100);
}

// Default export
export default {
    displayFiles,
    highlightFile
};
